import { FileCode, Sparkles, FileDiff, X as XIcon, Check, Terminal, Play, GitCommit, Square } from "lucide-react";

export function buildBlocks(events) {
  const blocks = [];
  let cur = null;
  for (const e of events) {
    switch (e.kind) {
      case "prompt": blocks.push({ type: "prompt", text: e.text }); cur = null; break;
      case "action": blocks.push({ type: "action", tool: e.tool, detail: e.detail }); break;
      case "narrative_start": cur = { type: "assistant", text: "", model: e.model }; blocks.push(cur); break;
      case "narrative_delta": if (cur) cur.text += e.text; break;
      case "narrative_end": cur = null; break;
      case "diff_proposal": blocks.push({ type: "diff", ...e }); cur = null; break;
      case "applied": blocks.push({ type: "applied", file: e.file }); break;
      case "rejected": blocks.push({ type: "rejected", file: e.file, command: e.command }); break;
      case "command_proposal": blocks.push({ type: "command", ...e }); cur = null; break;
      case "checkpoint": blocks.push({ type: "checkpoint", label: e.label, files: e.files }); break;
      case "complete": blocks.push({ type: "complete", summary: e.summary }); cur = null; break;
      case "cancelled": blocks.push({ type: "cancelled" }); cur = null; break;
      case "error": blocks.push({ type: "error", text: e.text }); cur = null; break;
      default: break;
    }
  }
  return blocks;
}

function cleanNarrative(text) {
  return text
    .replace(/<<<CADENCE_EDIT[\s\S]*?<<<END_CADENCE_EDIT>>>/g, "")
    .replace(/<<<CADENCE_RUN[\s\S]*?<<<END_CADENCE_RUN>>>/g, "")
    .replace(/<<<CADENCE_READ[\s\S]*?<<<END_CADENCE_READ>>>/g, "")
    .replace(/<<<CADENCE_LIST[\s\S]*?<<<END_CADENCE_LIST>>>/g, "")
    .replace(/<<<CADENCE_SEARCH[\s\S]*?<<<END_CADENCE_SEARCH>>>/g, "")
    .replace(/^SUMMARY:.*$/gim, "")
    .trim();
}

export function Block({ b, resolved, onApprove }) {
  if (b.type === "prompt")
    return (
      <div className="flex justify-end mb-6 animate-fade-up">
        <div className="bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]">{b.text}</div>
      </div>
    );
  if (b.type === "action")
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3 animate-fade-up">
        <FileCode className="w-3.5 h-3.5" /> <span className="font-mono">{b.tool}</span> · {b.detail}
      </div>
    );
  if (b.type === "assistant") {
    const text = cleanNarrative(b.text);
    return (
      <div className="mb-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-2 label-caps"><Sparkles className="w-3.5 h-3.5 text-blue-600" /> Cadence{b.model ? ` · ${b.model}` : ""}</div>
        <Markdown text={text || "…"} />
      </div>
    );
  }
  if (b.type === "diff") {
    const state = resolved[b.proposal_id];
    return (
      <div data-testid="diff-card" className="mb-6 border border-gray-200 rounded-xl overflow-hidden animate-fade-up">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2 text-sm"><FileDiff className="w-4 h-4 text-gray-500" /> <span className="font-mono text-[13px]">{b.file}</span></div>
          {!state ? (
            <div className="flex items-center gap-1.5">
              <button data-testid="diff-reject" onClick={() => onApprove(b.proposal_id, false)} className="text-xs px-2.5 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 inline-flex items-center gap-1"><XIcon className="w-3 h-3" /> Reject</button>
              <button data-testid="diff-approve" onClick={() => onApprove(b.proposal_id, true)} className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>
            </div>
          ) : (
            <span className={`text-xs font-600 ${state === "approved" ? "text-emerald-700" : "text-gray-400"}`}>{state === "approved" ? "Merged" : "Discarded"}</span>
          )}
        </div>
        <DiffView diff={b.diff} />
      </div>
    );
  }
  if (b.type === "command") {
    const state = resolved[b.proposal_id];
    return (
      <div className="mb-6 border border-gray-200 rounded-xl overflow-hidden animate-fade-up">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2 text-sm"><Terminal className="w-4 h-4 text-gray-500" /> Run command</div>
          {!state ? (
            <div className="flex items-center gap-1.5">
              <button onClick={() => onApprove(b.proposal_id, false)} className="text-xs px-2.5 py-1 rounded-md border text-gray-600 hover:bg-gray-100">Skip</button>
              <button data-testid="command-approve" onClick={() => onApprove(b.proposal_id, true)} className="text-xs px-2.5 py-1 rounded-md bg-gray-900 text-white inline-flex items-center gap-1"><Play className="w-3 h-3" /> Run</button>
            </div>
          ) : <span className="text-xs text-gray-400">{state === "approved" ? "Ran" : "Skipped"}</span>}
        </div>
        <pre className="bg-[#0A0A0A] text-gray-200 p-3 text-xs font-mono overflow-x-auto cadence-scroll">{b.command}</pre>
      </div>
    );
  }
  if (b.type === "applied")
    return <div className="text-xs text-emerald-700 mb-3 flex items-center gap-1.5 animate-fade-up"><Check className="w-3.5 h-3.5" /> Merged <span className="font-mono">{b.file}</span></div>;
  if (b.type === "rejected")
    return <div className="text-xs text-gray-400 mb-3 flex items-center gap-1.5 animate-fade-up"><XIcon className="w-3.5 h-3.5" /> Discarded {b.file ? <span className="font-mono">{b.file}</span> : "command"}</div>;
  if (b.type === "checkpoint")
    return <div className="text-xs text-gray-500 mb-4 inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-md px-2 py-1 animate-fade-up"><GitCommit className="w-3.5 h-3.5" /> {b.label}</div>;
  if (b.type === "complete")
    return (
      <div data-testid="task-complete" className="mb-6 flex items-center gap-1.5 text-xs text-gray-400 animate-fade-up">
        <Check className="w-3.5 h-3.5 text-emerald-500" /> Done
      </div>
    );
  if (b.type === "error")
    return <div className="mb-6 border border-red-200 bg-red-50 rounded-xl px-4 py-3 text-sm text-red-700 animate-fade-up">{b.text}</div>;
  if (b.type === "cancelled")
    return <div className="mb-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 animate-fade-up"><Square className="w-3 h-3" /> Task interrupted</div>;
  return null;
}

function Markdown({ text }) {
  const parts = text.split(/```/);
  return (
    <div className="text-[15px] leading-relaxed text-gray-700 space-y-2">
      {parts.map((seg, i) =>
        i % 2 === 1 ? (
          <pre key={i} className="bg-gray-900 text-gray-100 rounded-lg p-3 text-[13px] overflow-x-auto cadence-scroll font-mono">{seg.replace(/^\w*\n/, "")}</pre>
        ) : (
          <div key={i} className="whitespace-pre-wrap">{seg.trim()}</div>
        )
      )}
    </div>
  );
}

export function DiffView({ diff }) {
  const lines = (diff || "").split("\n");
  return (
    <pre className="text-[12px] font-mono overflow-x-auto cadence-scroll max-h-72">
      {lines.map((l, i) => {
        let cls = "px-3 py-0.5 block";
        if (l.startsWith("+") && !l.startsWith("+++")) cls += " diff-add";
        else if (l.startsWith("-") && !l.startsWith("---")) cls += " diff-del";
        else if (l.startsWith("@@")) cls += " text-blue-600 bg-blue-50";
        else cls += " text-gray-500";
        return <span key={i} className={cls}>{l || " "}</span>;
      })}
    </pre>
  );
}
